"use client";

// What the sign-in screen shows when the API refused this account, rather than
// when sign-in merely failed.
//
// The distinction is the point. A refusal used to render the same lone "Sign in"
// button as everything else, and that button cannot help: the auth service
// session is still live and consent is off for first-party clients, so it
// returns the same person to the same refusal with no prompt in between. The
// only action that changes the outcome is ending the provider session, which is
// what Sign out does here.
//
// Shared rather than copied into each vertical's login page: three admin apps,
// one behaviour, and a fix that reached two of them would be worse than none.
import { getEndSessionUrl } from "../../auth";
import { AUTH_SERVICE_URL } from "../../config";
import { useTranslation } from "../../i18n/context";
import { adminRuntime } from "../runtime";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";

// Trailing slash: /account is a 301 to /account/ at the auth service. Empty when
// the build has no auth service configured, in which case no link is rendered
// rather than one pointing at this app's own origin.
const ACCOUNT_URL = AUTH_SERVICE_URL ? `${AUTH_SERVICE_URL.replace(/\/$/, "")}/account/` : "";

/** The ?error= values the admin API's refusals map to. */
export type RefusalCode = "access_denied" | "account_inactive";

export function isRefusal(code: string | null): code is RefusalCode {
  return code === "access_denied" || code === "account_inactive";
}

export default function SignInRefusal({ code }: { code: RefusalCode }) {
  const { t, lang } = useTranslation();

  // Ending the auth service session, not just our own. Dropping the local
  // tokens alone leaves that session standing, and the next sign-in returns the
  // same person with no prompt — which reads as sign-out not working. The
  // provider sends the browser back to /auth/callback, which routes a code-less
  // LOGOUT_STATE arrival on to /login.
  const signOut = () => {
    const rt = adminRuntime();
    const url = getEndSessionUrl(
      rt.getIdToken(),
      `${window.location.origin}/auth/callback`,
      lang,
    );
    rt.clearTokens();
    window.location.href = url;
  };

  return (
    <>
      <Alert severity="error" sx={{ mb: 2 }}>
        {code === "account_inactive" ? t("accountDeactivated") : t("accessDenied")}
      </Alert>
      {/* No "sign in" alongside these: retrying cannot lift a refusal, and
          offering it invites a loop the reader cannot win. */}
      <Button variant="contained" fullWidth type="button" onClick={signOut}>
        {t("logout")}
      </Button>
      {ACCOUNT_URL && (
        <Button variant="outlined" fullWidth component="a" href={ACCOUNT_URL} sx={{ mt: 1 }}>
          {t("yourAccount")}
        </Button>
      )}
    </>
  );
}
